// Tiny export helpers for Excel-compatible files (Tunisia, French locale).
import { isoDate } from "./date";
// - buildCsv/downloadCsv: legacy CSV with ';' separator + UTF-8 BOM
// - downloadXlsx: real .xlsx via SheetJS (no extra setup needed by user)
import * as XLSX from "xlsx";

function csvCell(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (s.includes(";") || s.includes('"') || s.includes("\n") || s.includes(",")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Build a CSV string from rows + ordered column definitions.
 * @param {Array<{key: string, label: string, format?: (v:any, row:any)=>string}>} columns
 * @param {Array<object>} rows
 */
export function buildCsv(columns, rows) {
  const header = columns.map((c) => csvCell(c.label)).join(";");
  const body = rows
    .map((row) =>
      columns
        .map((c) => {
          const raw = c.format ? c.format(row[c.key], row) : row[c.key];
          return csvCell(raw);
        })
        .join(";"),
    )
    .join("\r\n");
  return header + "\r\n" + body;
}

export function downloadCsv(filename, csv) {
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * Build and download a real .xlsx file via SheetJS.
 * Numbers stay as numbers (Excel does the formatting), strings stay strings.
 * @param {string} filename — without extension
 * @param {Array<{key: string, label: string, format?: (v:any, row:any)=>any, type?: 'string'|'number'|'date'}>} columns
 * @param {Array<object>} rows
 * @param {string} [sheetName] — sheet tab name, default "Données"
 */
export function downloadXlsx(filename, columns, rows, sheetName = "Données") {
  const aoa = [columns.map((c) => c.label)];
  for (const row of rows) {
    aoa.push(
      columns.map((c) => {
        const raw = c.format ? c.format(row[c.key], row) : row[c.key];
        if (raw === null || raw === undefined) return "";
        // Keep numbers as numbers when type='number'
        if (c.type === "number") {
          const n = Number(raw);
          return Number.isFinite(n) ? n : "";
        }
        return raw;
      }),
    );
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Auto column width based on header length (approx)
  ws["!cols"] = columns.map((c) => ({
    wch: Math.max(c.label.length + 2, 12),
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 30));
  const finalName = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
  XLSX.writeFile(wb, finalName);
}

// Format helpers (FR locale: comma decimals, dd/mm/yyyy dates).
export function fmtMoney(v) {
  const n = Number(v || 0);
  return n.toFixed(2).replace(".", ",");
}

export function fmtDate(v) {
  if (!v) return "";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}/${d.getFullYear()}`;
  } catch {
    return String(v);
  }
}

export function fmtBool(v) {
  return v ? "OUI" : "NON";
}

export function todayStamp() {
  return isoDate();
}
